/**
 * Movie detail.
 *
 * The panel's `get_vod_info` is authoritative for metadata but routinely sparse,
 * so every field falls back to the catalog row and then simply disappears —
 * an empty "Director:" line is worse than no line.
 */

import { useEffect, useState } from 'react';
import type { StreamDto } from '@sflx/shared';
import { ErrorNotice, Loading } from '../components/Feedback';
import { FavoriteButton } from '../components/FavoriteButton';
import { Icon } from '../components/Icon';
import { catalog, state, type MovieDetailResponse } from '../lib/api';
import { formatClock, formatDuration } from '../lib/format';
import { watchLink } from '../lib/links';
import { navigate } from '../router/router';

export function MovieDetail({ accountId, id }: { accountId: string; id: number }) {
  const [data, setData] = useState<MovieDetailResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [stream, setStream] = useState<StreamDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    catalog
      .movie(accountId, id)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStream(result.stream);
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, id]);

  if (error) {
    return (
      <div className="page">
        <div className="wrap stack">
          <ErrorNotice error={error} />
          <button type="button" className="btn" onClick={() => navigate('/movies')}>
            Back to movies
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <Loading label="Loading movie" />
      </div>
    );
  }

  const detail = data.detail;
  const title = detail.title ?? stream?.name ?? 'Untitled';
  const poster = detail.coverUrl ?? stream?.logoUrl ?? null;
  const year = detail.releaseDate ? detail.releaseDate.slice(0, 4) : (stream?.year ?? null);
  const rating = detail.rating ?? stream?.rating ?? null;
  const duration = formatDuration(detail.durationS);
  const resumeFrom = stream?.positionS ?? 0;
  const container = detail.container ?? stream?.container ?? null;

  const target = watchLink({ accountId, kind: 'movie', xtreamId: id, container, title });

  /**
   * "Start over" clears the stored position before navigating: the play URL is
   * minted server-side and carries `resumeFrom`, so the only way to start at
   * zero is to make the server agree there is nothing to resume.
   */
  const startOver = async () => {
    try {
      await state.clearProgress({ accountId, kind: 'movie', xtreamId: id });
    } catch {
      // Losing the reset is survivable; playback still starts.
    }
    navigate(target);
  };

  return (
    <div className="page">
      <div className="wrap stack" style={{ gap: 'var(--s-6)' }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/movies')} style={{ alignSelf: 'flex-start' }}>
          <Icon name="chevronLeft" size={15} />
          Movies
        </button>

        <div className="detail__hero">
          <div className="detail__poster">
            {poster ? <img src={poster} alt="" loading="lazy" decoding="async" /> : null}
          </div>

          <div className="stack">
            <div className="stack stack--tight" style={{ gap: 'var(--s-2)' }}>
              <h1>{title}</h1>
              <div className="row muted">
                {year ? <span>{year}</span> : null}
                {rating ? <span>{rating.toFixed(1)}</span> : null}
                {duration ? <span>{duration}</span> : null}
                {detail.genre ? <span>{detail.genre}</span> : null}
              </div>
            </div>

            <div className="row">
              <button type="button" className="btn btn--primary" onClick={() => navigate(target)}>
                <Icon name="play" size={16} />
                {resumeFrom > 0 ? `Resume at ${formatClock(resumeFrom)}` : 'Play'}
              </button>
              {resumeFrom > 0 ? (
                <button type="button" className="btn" onClick={() => void startOver()}>
                  Start over
                </button>
              ) : null}
              {stream ? (
                <FavoriteButton
                  accountId={accountId}
                  kind="movie"
                  xtreamId={id}
                  favorite={stream.favorite}
                  onChange={(favorite) => setStream({ ...stream, favorite })}
                  size={20}
                />
              ) : null}
            </div>

            {detail.plot ? <p className="detail__plot">{detail.plot}</p> : null}

            <dl className="kv">
              {detail.director ? (
                <>
                  <dt>Director</dt>
                  <dd>{detail.director}</dd>
                </>
              ) : null}
              {detail.cast ? (
                <>
                  <dt>Cast</dt>
                  <dd>{detail.cast}</dd>
                </>
              ) : null}
              {detail.releaseDate ? (
                <>
                  <dt>Released</dt>
                  <dd>{detail.releaseDate}</dd>
                </>
              ) : null}
              {container ? (
                <>
                  <dt>Container</dt>
                  <dd className="mono">{container}</dd>
                </>
              ) : null}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
